import React, { useMemo } from 'react';
import { useActivityLogs, ActivityLog } from '../../activity-logs/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface ActivityByEntityTypeProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  limit?: number;
}

const ActivityByEntityType: React.FC<ActivityByEntityTypeProps> = ({
  embedded,
  settings,
  limit = 10,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: logs, isLoading } = useActivityLogs();
  const { colors } = useTheme();

  const chartData = useMemo(() => {
    const arr = Array.isArray(logs) ? logs : [];
    const byEntity: Record<string, number> = {};
    arr.forEach((log: ActivityLog) => {
      const type = log.entityType || 'Other';
      byEntity[type] = (byEntity[type] || 0) + 1;
    });
    return Object.entries(byEntity)
      .map(([entityType, count]) => ({ entityType, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, effectiveLimit);
  }, [logs, effectiveLimit]);

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Activity by Entity
        </h2>
        <div className="h-48 flex items-center justify-center">
          <div className={`animate-pulse ${colors.textMuted}`}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Activity by Entity
        </h2>
        <div className="h-48 flex items-center justify-center">
          <p className={colors.textMuted}>No activity</p>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...chartData.map((d) => d.count), 1);

  return (
    <div className={cardClass}>
      <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
        Activity by Entity
      </h2>
      <div className="flex flex-col gap-2">
        {chartData.map((d) => (
          <div key={d.entityType} className="flex items-center gap-3">
            <span className={`text-xs flex-shrink-0 w-24 ${colors.text}`}>
              {d.entityType}
            </span>
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <div
                className="h-5 rounded bg-teal-500 dark:bg-teal-600 flex-shrink-0 transition-all"
                style={{
                  width: `${Math.max(4, (d.count / maxCount) * 100)}%`,
                  minWidth: d.count > 0 ? 8 : 0,
                }}
              />
              <span className={`text-xs ${colors.textMuted} flex-shrink-0`}>
                {d.count}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityByEntityType;
